import { defineStore } from 'pinia'
import { ref } from 'vue'

import type { ParsedError } from '@/backend/error/types'
import type { ClassMapping } from '@/modules/Workview/classMappingTypes'
import { HARDWARE_CONCURRENCY, NEIGHBOUR_TILES } from '@/utils/localStorageKeys'
import { PersistedSettings } from '@/core/utils/PersistedSettings'

const persistedSettings = new PersistedSettings()

const getInitialClickerEpsilon = (): number => 0.5
const getInitialPreselectedModelId = () => null
const getInitialNeighbourTiles = () => persistedSettings.loadValue(NEIGHBOUR_TILES, false)
const getInitialHardwareConcurrency = () =>
  persistedSettings.loadValue(HARDWARE_CONCURRENCY, navigator.hardwareConcurrency - 1 || 2)
const getInitialError = () => null
const getInitialAutoAnnotateClassMapping = (): { [runningSessionId: string]: ClassMapping } =>
  JSON.parse(localStorage.getItem('class-mapping') || '{}')

export const useWorkviewStore = defineStore('workviewStore', () => {
  const clickerEpsilon = ref(getInitialClickerEpsilon())
  const setClickerEpsilon = (epsilon: number): void => {
    clickerEpsilon.value = epsilon
  }

  const preselectedModelId = ref<null | string>(getInitialPreselectedModelId())
  const setCurrentToolPreselectedModelId = (_preselectedModelId: null | string): void => {
    preselectedModelId.value = _preselectedModelId
  }

  const neighbourTiles = ref(getInitialNeighbourTiles())
  const setNeighbourTiles = (_neighbourTiles: boolean): void => {
    persistedSettings.saveValue(NEIGHBOUR_TILES, _neighbourTiles)
    neighbourTiles.value = _neighbourTiles
  }

  const hardwareConcurrency = ref(getInitialHardwareConcurrency())
  const setHardwareConcurrency = (_hardwareConcurrency: number): void => {
    persistedSettings.saveValue(HARDWARE_CONCURRENCY, _hardwareConcurrency)
    hardwareConcurrency.value = _hardwareConcurrency
  }

  const error = ref<ParsedError['error'] | null>(getInitialError())
  const setError = (_error: ParsedError['error']): void => {
    error.value = _error
  }

  const autoAnnotateClassMapping = ref(getInitialAutoAnnotateClassMapping())
  const setAutoAnnotateClassMapping = (instance: {
    runningSessionId: string
    classMapping: ClassMapping
  }): void => {
    autoAnnotateClassMapping.value[instance.runningSessionId] = instance.classMapping
    localStorage.setItem('class-mapping', JSON.stringify(autoAnnotateClassMapping.value))
  }

  const resetState = (): void => {
    clickerEpsilon.value = getInitialClickerEpsilon()
    preselectedModelId.value = getInitialPreselectedModelId()
    neighbourTiles.value = getInitialNeighbourTiles()
    hardwareConcurrency.value = getInitialHardwareConcurrency()
    error.value = getInitialError()
    autoAnnotateClassMapping.value = getInitialAutoAnnotateClassMapping()
  }

  return {
    setAutoAnnotateClassMapping,
    setError,
    setClickerEpsilon,
    setHardwareConcurrency,
    setNeighbourTiles,
    resetState,
    setCurrentToolPreselectedModelId,
    hardwareConcurrency,
    preselectedModelId,
    clickerEpsilon,
    neighbourTiles,
    autoAnnotateClassMapping,
    error,
  }
})
